'use client';
import { useState } from 'react';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

type UseMediaQueryOptions = {
  defaultValue?: boolean;
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === 'undefined';

/**
 * Tracks whether the viewport currently matches a CSS media query string.
 *
 * Re-evaluates on media query changes (e.g. a viewport resize crossing a breakpoint, or the OS
 * theme changing for a `prefers-color-scheme` query) and re-renders with the new value.
 *
 * For the common case of checking against a Spellbook breakpoint token, prefer `useBreakpoint`,
 * which builds on this hook and takes a breakpoint key instead of a raw query string.
 *
 * @param query - A CSS media query string, e.g. `'(min-width: 768px)'`.
 * @param options.defaultValue - Value returned during SSR and before the first client match. Defaults to `false`.
 * @param options.initializeWithValue - When `false`, skips reading `matchMedia` on the initial render (avoids a hydration mismatch when the server-rendered value must stay in sync with `defaultValue`). Defaults to `true`.
 * @returns `true` when the query currently matches.
 *
 * @example
 * ```tsx
 * const isWide = useMediaQuery('(min-width: 1024px)');
 * const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
 * ```
 *
 * @example
 * ```tsx
 * // Server renders `false`; skip re-checking on the client so hydration always matches.
 * const isPrintPreview = useMediaQuery('print', { defaultValue: false, initializeWithValue: false });
 * ```
 */
export function useMediaQuery(query: string, { defaultValue = false, initializeWithValue = true }: UseMediaQueryOptions = {}): boolean {
  const getMatches = (q: string): boolean => {
    if (IS_SERVER) return defaultValue;
    return window.matchMedia(q).matches;
  };

  const [matches, setMatches] = useState<boolean>(() => (initializeWithValue ? getMatches(query) : defaultValue));

  useIsomorphicLayoutEffect(() => {
    const mq = window.matchMedia(query);
    const handleChange = () => setMatches(mq.matches);
    handleChange();
    if (mq.addListener) {
      mq.addListener(handleChange);
    } else {
      mq.addEventListener('change', handleChange);
    }
    return () => {
      if (mq.removeListener) {
        mq.removeListener(handleChange);
      } else {
        mq.removeEventListener('change', handleChange);
      }
    };
  }, [query]);

  return matches;
}
